import React from "react";
import PropTypes from "prop-types";
import {
    AutocompleteField,
    CheckboxField,
    Form as FormikForm,
    InputField,
    SelectField,
    TextareaField,
} from "components/common_components/Formik";
import Grid from "@material-ui/core/Grid";
import * as Yup from "yup";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { Field } from "formik";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { getUnitsOfTime } from "utils/dateUtils";

function Form({ data, resources, mode, ...rest }) {
    const dueDateCalculationValidation = (values) => (value, context) => {
        if (!values.dueUnitOfTime || !values.dueInterval) return false;
        return true;
    };

    const validationSchema = Yup.lazy((values) =>
        Yup.object({
            id: Yup.string().required("Required field").max(20, "Max length is 20 characters"),
            type: Yup.string().required("Required field").max(50, "Max length is 50 characters"),
            evidenceDocTypes: Yup.array().of(Yup.object()),
            overrides: Yup.lazy((values) => {
                const keys = Object.keys(values);
                return Yup.object().shape(
                    keys.reduce((acc, key) => {
                        acc[key] = Yup.object().shape({
                            description: Yup.string().notRequired().max(1998, "Max length is 1998 characters"),
                            enabled: Yup.boolean().notRequired(),
                        });
                        return acc;
                    }, {})
                );
            }),
            dueInterval:
                values.dueUnitOfTime || Boolean(values.notifyBeforeDue)
                    ? Yup.number()
                          .nullable()
                          .integer("Decimals are not allowed here")
                          .positive("A positive number is required")
                          .min(1, "The interval must be greater than or equal to 1")
                          .required("Required field")
                    : Yup.number().nullable(),
            dueUnitOfTime:
                values.dueInterval || Boolean(values.notifyBeforeDue)
                    ? Yup.string().required("Required field")
                    : Yup.string().nullable(),
            notifyClientOnClose: Yup.boolean().notRequired().nullable(),
            notifyBeforeDue: Yup.boolean().notRequired().nullable(),
            notifyBeforeDueInterval: Boolean(values.notifyBeforeDue)
                ? Yup.number()
                      .nullable()
                      .integer("Decimals are not allowed here")
                      .positive("A positive number is required")
                      .min(1, "The interval must be greater than or equal to 1")
                      .required("Required field")
                      .test(
                          "is-due-date-calculation-valid",
                          "Due Date Calculation values are required",
                          dueDateCalculationValidation(values)
                      )
                : Yup.number().nullable(),
            notifyBeforeDueUnitOfTime: Boolean(values.notifyBeforeDue)
                ? Yup.string()
                      .nullable()
                      .required("Required field")
                      .test(
                          "is-due-date-calculation-valid",
                          "Due Date Calculation values are required",
                          dueDateCalculationValidation(values)
                      )
                : Yup.string().nullable(),
        })
    );

    const initialValues = {
        id: data.id,
        type: data.type,
        dueInterval: data.dueInterval,
        dueUnitOfTime: data.dueUnitOfTime,
        notifyClientOnClose: data.notifyClientOnClose,
        notifyBeforeDue: data.notifyBeforeDue,
        notifyBeforeDueInterval: data.notifyBeforeDueInterval,
        notifyBeforeDueUnitOfTime: data.notifyBeforeDueUnitOfTime,
        evidenceDocType: null,
        evidenceDocTypes: data.evidenceDocTypes.map((evidenceDocType) => ({
            ...evidenceDocType,
            type: evidenceDocType.cocEvidenceDocType.type,
        })),
        overrides: data.evidenceDocTypes.reduce((acc, evidenceDocType) => {
            acc[evidenceDocType.settingsCocEvidenceDocTypesId] = {
                description: evidenceDocType.descriptionOverride || evidenceDocType.cocEvidenceDocType.description,
            };
            return acc;
        }, {}),
    };

    return (
        <FormikForm initialValues={initialValues} validationSchema={validationSchema} {...rest}>
            {(form) => (
                <Grid container spacing={3} direction="column">
                    <Grid item xs={12}>
                        <Grid container spacing={3} alignItems="center" className="identity">
                            <Grid item xs={12} md={8} lg={6}>
                                <InputField
                                    disabled={mode === "update"}
                                    fieldName="id"
                                    label="Identifier *"
                                    uppercase
                                    fullWidth={false}
                                    maxLength={20}
                                />
                                <InputField fieldName="type" label="Type *" maxLength={50} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={3} alignItems="center" className="deadline">
                            <Grid item xs={12}>
                                <div className="title">Deadline</div>
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <InputField fieldName="dueInterval" label="Interval" type="number" />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <SelectField fieldName="dueUnitOfTime" label="Unit of time" options={getUnitsOfTime(true)} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={3} alignItems="center" className="notifications">
                            <Grid item xs={12}>
                                <div className="title">Notifications</div>
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <CheckboxField fieldName="notifyBeforeDue" label="Notify due date" />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <InputField
                                    disabled={!Boolean(form.values.notifyBeforeDue)}
                                    fieldName="notifyBeforeDueInterval"
                                    label="Interval"
                                    type="number"
                                />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <SelectField
                                    disabled={!Boolean(form.values.notifyBeforeDue)}
                                    fieldName="notifyBeforeDueUnitOfTime"
                                    label="Unit of time"
                                    options={getUnitsOfTime(true)}
                                />
                            </Grid>
                            {/* <Grid item xs={12}>
                                <CheckboxField fieldName="notifyClientOnClose" label="Notify client on close" />
                            </Grid> */}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={3} direction="row" alignItems="center" className="evidence-types">
                            <Grid item xs={11} md={10} lg={8}>
                                <AutocompleteField
                                    fieldName="evidenceDocType"
                                    options={resources.evidenceTypes.filter(
                                        (e) => !form.values.evidenceDocTypes.some((et) => et.settingsCocEvidenceDocTypesId === e.id)
                                    )}
                                    valueKey="id"
                                    labelKey="type"
                                    label="Choose an evidence type and click on the plus sign to add it"
                                />
                            </Grid>
                            <Grid item xs={1}>
                                {form.values.evidenceDocType && (
                                    <Button
                                        outline
                                        small
                                        secondary
                                        onClick={() => {
                                            form.setFieldValue("evidenceDocTypes", [
                                                ...form.values.evidenceDocTypes,
                                                {
                                                    id: null,
                                                    settingsCocRequestTypesId: data.id,
                                                    settingsCocEvidenceDocTypesId: form.values.evidenceDocType.id,
                                                    type: form.values.evidenceDocType.type,
                                                    descriptionOverride: form.values.evidenceDocType.description,
                                                },
                                            ]);
                                            form.setFieldValue(
                                                `overrides.${form.values.evidenceDocType.id}.description`,
                                                form.values.evidenceDocType.description
                                            );
                                            form.setFieldValue(`overrides.${form.values.evidenceDocType.id}.enabled`, false);
                                            form.setFieldValue("evidenceDocType", null);
                                        }}
                                    >
                                        <Icon name="add" size="sm" color="secondary" />
                                    </Button>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Field name="evidenceDocTypes">
                                    {({ field }) => (
                                        <EnhancedTable
                                            headCells={[
                                                { index: 1, id: "settingsCocEvidenceDocTypesId", label: "Evidence Types" },
                                                { index: 2, id: "type", label: "" },
                                                {
                                                    index: 3,
                                                    id: "descriptionOverride",
                                                    label: "",
                                                    render: (_, { value, data: fieldData }) => (
                                                        <Grid container spacing={1} direction="row" alignItems="center">
                                                            <Grid item xs={12}>
                                                                {form.values["overrides"][fieldData.settingsCocEvidenceDocTypesId]
                                                                    ?.enabled ? (
                                                                    <TextareaField
                                                                        fieldName={`overrides.${fieldData.settingsCocEvidenceDocTypesId}.description`}
                                                                        label=""
                                                                        maxLength={2000}
                                                                        rows={3}
                                                                    />
                                                                ) : (
                                                                    <InputField
                                                                        fieldName={`overrides.${fieldData.settingsCocEvidenceDocTypesId}.description`}
                                                                        label=""
                                                                        maxLength={2000}
                                                                        disabled
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    ),
                                                },
                                                {
                                                    index: 4,
                                                    id: "actions",
                                                    label: "",
                                                    render: (_, { data }) => (
                                                        <>
                                                            {form.values["overrides"][data.settingsCocEvidenceDocTypesId]?.enabled ? (
                                                                <Grid container spacing={1} direction="column" justifyContent="flex-start">
                                                                    <Grid item>
                                                                        <Button
                                                                            outline
                                                                            small
                                                                            secondary
                                                                            onClick={() =>
                                                                                form.setFieldValue(
                                                                                    `overrides.${data.settingsCocEvidenceDocTypesId}.enabled`,
                                                                                    false
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon name="save" size="sm" color="secondary" />
                                                                        </Button>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Button
                                                                            outline
                                                                            small
                                                                            secondary
                                                                            title="Reset"
                                                                            onClick={() => {
                                                                                form.setFieldValue(
                                                                                    `overrides.${data.settingsCocEvidenceDocTypesId}.description`,
                                                                                    data.description
                                                                                );
                                                                                form.setFieldValue(
                                                                                    `overrides.${data.settingsCocEvidenceDocTypesId}.enabled`,
                                                                                    false
                                                                                );
                                                                            }}
                                                                        >
                                                                            <Icon name="cancel" size="sm" color="secondary" />
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            ) : (
                                                                <Grid container spacing={1} direction="row" alignItems="center">
                                                                    <Grid item md={12} lg={4}>
                                                                        <Button
                                                                            outline
                                                                            small
                                                                            secondary
                                                                            onClick={() =>
                                                                                form.setFieldValue(
                                                                                    `overrides.${data.settingsCocEvidenceDocTypesId}.enabled`,
                                                                                    true
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon name="pencil" size="sm" color="secondary" />
                                                                        </Button>
                                                                    </Grid>
                                                                    <Grid item md={12} lg={4}>
                                                                        <Button
                                                                            outline
                                                                            small
                                                                            secondary
                                                                            onClick={() =>
                                                                                form.setFieldValue(
                                                                                    "evidenceDocTypes",
                                                                                    field.value.filter(
                                                                                        (e) =>
                                                                                            e.settingsCocEvidenceDocTypesId !==
                                                                                            data.settingsCocEvidenceDocTypesId
                                                                                    )
                                                                                )
                                                                            }
                                                                        >
                                                                            <Icon name="delete" size="sm" color="secondary" />
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            )}
                                                        </>
                                                    ),
                                                },
                                            ]}
                                            data={field.value}
                                            selectable={false}
                                            pageable={false}
                                            filterable={false}
                                            deletable={false}
                                            orderable={false}
                                            keyColumnName="id"
                                            onRequestData={() => field.value}
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </FormikForm>
    );
}

Form.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        evidenceDocTypes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                settingsCocRequestTypesId: PropTypes.string,
                settingsCocEvidenceDocTypesId: PropTypes.string,
                descriptionOverride: PropTypes.string,
                cocEvidenceDocType: PropTypes.shape({
                    id: PropTypes.string,
                    type: PropTypes.string,
                    description: PropTypes.string,
                }),
            })
        ),
        dueInterval: PropTypes.number,
        dueUnitOfTime: PropTypes.string,
        notifyClientOnClose: PropTypes.bool,
        notifyBeforeDue: PropTypes.bool,
        notifyBeforeDueInterval: PropTypes.number,
        notifyBeforeDueUnitOfTime: PropTypes.string,
    }),
    resources: PropTypes.shape({
        evidenceTypes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                type: PropTypes.string,
                description: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
};

Form.defaultProps = {
    data: {
        id: "",
        type: "",
        evidenceDocTypes: [],
        dueInterval: 60,
        dueUnitOfTime: "d",
        notifyClientOnClose: false,
        notifyBeforeDue: false,
        notifyBeforeDueInterval: 2,
        notifyBeforeDueUnitOfTime: "d",
    },
    resources: {
        evidenceTypes: [],
    },
};

export default Form;
